import React, { useState } from 'react';
import useSWR from 'swr';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import {
  fetchAvailableRedemptionsSWR,
  getAvailableRedemptionsUrl,
  handleRedemption,
} from './actions';
import RewardItem from './RewardItem';
import { getCurrentDayFlag } from 'helpers/timeHelper';
import './rewards-list.css';

const DAY_KEYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const normalizeKeyList = (keys) =>
  Array.isArray(keys) ? keys.map((key) => `${key}`) : [];

const hasDayRules = (voucherInfo) =>
  DAY_KEYS.some((dayKey) => typeof voucherInfo?.[dayKey] === 'boolean');

const isVoucherValidForToday = (voucherInfo, timeZone) => {
  if (!voucherInfo) return false;
  if (!hasDayRules(voucherInfo)) return false;
  if (!timeZone) return false;
  const { dayText } = getCurrentDayFlag(timeZone) || {};
  if (!dayText) return false;
  return Boolean(voucherInfo?.[dayText]);
};

const isVoucherValidForOrderType = (voucherInfo, orderType) => {
  if (!voucherInfo?.orderTypeKeys?.length) return false;
  if (!orderType) return false;
  return normalizeKeyList(voucherInfo.orderTypeKeys).includes(`${orderType}`);
};

const isVoucherValidForOrderFrom = (voucherInfo, orderFrom) => {
  if (!voucherInfo?.orderFromKeys?.length) return false;
  if (!orderFrom) return false;
  return normalizeKeyList(voucherInfo.orderFromKeys).includes(`${orderFrom}`);
};

const isVoucherActive = (voucherInfo) => {
  if (!voucherInfo) return false;
  return voucherInfo.isActive !== false;
};

const isVoucherAvailable = (voucherInfo, timeZone) => {
  if (!voucherInfo) return false;
  const { endDateTimestamp, endDate } = voucherInfo || {};
  if (!endDateTimestamp && !endDate) return false;
  if (!timeZone) return false;
  // Treat endDate as inclusive so rewards remain valid through the end of day.
  const today = moment().tz(timeZone).startOf('day');
  const endMoment = endDateTimestamp
    ? moment(endDateTimestamp).tz(timeZone).endOf('day')
    : moment(endDate, 'YYYY-MM-DD').tz(timeZone).endOf('day');
  if (!endMoment.isValid()) return false;
  return endMoment.isSameOrAfter(today, 'day');
};

const RewardsList = ({
  userId,
  storeId,
  restaurantId,
  level,
  themeColors,
  userPoints,
  pointsTerminology = 'points',
  sortBy = 'points_asc',
  orderType,
  orderFrom,
  timeZone,
}) => {
  const dispatch = useDispatch();

  // Loading state for redemption
  const [redeemingId, setRedeemingId] = useState(null);

  // Get the URL for SWR to fetch available redemptions (without backend sorting)
  const redemptionsKey = getAvailableRedemptionsUrl(
    userId,
    storeId,
    restaurantId,
    level || 0
  );

  // Use SWR to fetch available redemptions
  const {
    data: availableRedemptions,
    error: availableRedemptionsError,
    isLoading: isLoadingAvailableRedemptions,
  } = useSWR(redemptionsKey, fetchAvailableRedemptionsSWR, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 10000, // dedupe requests within 10 seconds
  });

  const filteredRedemptions = React.useMemo(() => {
    if (!availableRedemptions || !Array.isArray(availableRedemptions)) {
      return [];
    }

    return availableRedemptions.filter((redemption) => {
      const voucherInfo = redemption?.voucherInfo;
      return (
        isVoucherActive(voucherInfo) &&
        isVoucherAvailable(voucherInfo, timeZone) &&
        isVoucherValidForToday(voucherInfo, timeZone) &&
        isVoucherValidForOrderType(voucherInfo, orderType) &&
        isVoucherValidForOrderFrom(voucherInfo, orderFrom)
      );
    });
  }, [availableRedemptions, orderType, orderFrom, timeZone]);

  // Client-side sorting
  const sortedRedemptions = React.useMemo(() => {
    if (!filteredRedemptions || !Array.isArray(filteredRedemptions)) {
      return [];
    }

    const sorted = [...filteredRedemptions].sort((a, b) => {
      if (sortBy === 'points_asc') {
        // Sort by points ascending (lowest first)
        const pointsA = a.pointsRequired || a.points || 0;
        const pointsB = b.pointsRequired || b.points || 0;
        return pointsA - pointsB;
      } else if (sortBy === 'name_asc') {
        // Sort by name ascending (A-Z)
        const nameA = (a.name || a.title || '').toLowerCase();
        const nameB = (b.name || b.title || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    return sorted;
  }, [filteredRedemptions, sortBy]);

  // Handle redemption with parameters as a single object
  const handleRedeemReward = async (redemptionId) => {
    try {
      // Set loading state for this specific redemption
      setRedeemingId(redemptionId);

      const result = await handleRedemption({
        redemptionId,
        userId,
        storeId,
        restaurantId,
        level: level || 0,
        dispatch, // Pass the dispatch function to handleRedemption
      });

      // Success handling is now done in the handleRedemption function
    } catch (error) {
      console.error('Error redeeming reward:', error);
      // Could display an error message to the user here
    } finally {
      // Clear loading state
      setRedeemingId(null);
    }
  };

  // Display loading state
  if (isLoadingAvailableRedemptions) {
    return (
      <div className='rewards-list-loading'>
        <div className='reward-placeholder'></div>
      </div>
    );
  }

  // Display error state
  if (availableRedemptionsError) {
    return (
      <div className='rewards-list-error'>
        Unable to load rewards. Please try again later.
      </div>
    );
  }

  // Display empty state if no redemptions available
  if (
    !isLoadingAvailableRedemptions &&
    (!sortedRedemptions || sortedRedemptions.length === 0)
  ) {
    return (
      <div className='rewards-list-empty'>
        <p>No rewards available at this time.</p>
      </div>
    );
  }

  return (
    <div className='rewards-list'>
      {sortedRedemptions && sortedRedemptions.length > 0 ? (
        sortedRedemptions.map((redemption) => (
          <RewardItem
            key={redemption.id}
            title={redemption.name}
            description={redemption.description}
            points={redemption.points}
            imageUrl={redemption.imageUrl}
            canRedeem={userPoints >= redemption.points && redeemingId === null}
            isLoading={redeemingId === redemption.id}
            isDisabled={redeemingId !== null && redeemingId !== redemption.id}
            onRedeem={() => handleRedeemReward(redemption.id)}
            themeColors={themeColors}
            pointsTerminology={pointsTerminology}
          />
        ))
      ) : (
        <div>No rewards available</div>
      )}
    </div>
  );
};

export default RewardsList;
